import { FoComboCardWithOdds } from '@staycool/sports-types/fo-combo-card';
import classNames from 'classnames';
import React from 'react';
import { useClearAllModal } from '../../../../hooks/betslip-v2';
import { useStore } from '../../../../hooks/useStore';
import { getMarketInfoByOutcomeId } from '../../../../microservices/sbgate';
import { isFeatureAvailable } from '../../../../services/feature';
import { convertOdds } from '../../../../services/odds-format';
import { clearBetslipSelection } from '../../../../services/sports/betslip';
import { removeSelectionFromBetslip } from '../../../../services/sports/combo-cards';
import { initialBetSlipPlacingState } from '../../../../services/sports/constants';
import { BET_TYPE, BetslipMode } from '../../../../services/sports/types';
import { translate } from '../../../../services/translate';
import { FEATURE } from '../../../../services/types';
import { isTestUser } from '../../../../services/user';
import { stores } from '../../../../stores';
import UiButton from '../../../ui/button/UiButton';
import Wrapper from './styles';

interface Props {
    card: FoComboCardWithOdds;
}

export default function ComboCardsItemFooter({ card }: Props) {
    const [cardsInBetslip, setCardsInBetslip] = useStore(stores.sports.comboCard.cardsInBetslip);
    const [betSlipMarketIdToOutcomeId, setBetSlipMarketIdToOutcomeId] = useStore(
        stores.sports.betSlipMarketIdToOutcomeId,
    );
    const [, setBetSlipUserState] = useStore(stores.sports.betSlipUserState);
    const [, setBetSlipPlacingState] = useStore(stores.sports.betSlipPlacingState);
    const cardAlreadyInBetslip = Boolean(cardsInBetslip.find(({ id }) => id === card.id));

    const clearAllModal = useClearAllModal();

    function handleComboCardSelectionV2() {
        if (Object.values(betSlipMarketIdToOutcomeId).length > 0 && !cardAlreadyInBetslip) {
            clearAllModal.show(handleComboCardSelection);
        } else {
            handleComboCardSelection();
        }
    }

    async function handleComboCardSelection() {
        if (!cardsInBetslip.length) {
            clearBetslipSelection(BetslipMode.Betslip, false, true);
        }

        setBetSlipPlacingState(initialBetSlipPlacingState);
        if (!cardAlreadyInBetslip) {
            setBetSlipUserState((prev) => ({
                ...prev,
                userBetTypeSelection: BET_TYPE.COMBO_CARD,
                betType: BET_TYPE.COMBO_CARD,
            }));
            setCardsInBetslip([card]);

            const markets = card.matches.flatMap(({ markets }) =>
                markets.map(({ id, outcome_id }) => ({ [id]: outcome_id })),
            );
            const marketIdToOutcomeId = Object.assign({}, ...markets);
            const outcomeIds = Object.values(marketIdToOutcomeId);
            setBetSlipMarketIdToOutcomeId(marketIdToOutcomeId);
            await Promise.all(outcomeIds.map(getMarketInfoByOutcomeId));
        } else {
            removeSelectionFromBetslip(card.id);
        }
    }

    return (
        <Wrapper>
            <UiButton
                className={classNames('selection-btn', { active: cardAlreadyInBetslip })}
                color="primary"
                size="small"
                block
                onClick={
                    isFeatureAvailable(FEATURE.BETSLIP_V2) && isTestUser()
                        ? handleComboCardSelectionV2
                        : handleComboCardSelection
                }
            >
                {card.odds_boost === 1 ? (
                    <span className="boosted-odds">{convertOdds(card.boosted_odds)}</span>
                ) : (
                    <>
                        <span className="original-odds">{convertOdds(card.original_odds)}</span>
                        <span className="arrow">{' » '}</span>
                        <span className="boosted-odds">{convertOdds(card.boosted_odds)}</span>
                    </>
                )}
            </UiButton>
            <div className="cards-left-text">
                <span className="amount-value">{card.bets_left}</span>{' '}
                {translate(['COMBO_CARD_CARDS_LEFT', 'cards left'], 'ui.sportsbook.combocard')}
            </div>
        </Wrapper>
    );
}
